import { currentUser } from './authModule/state/actions/authentication.actions';
import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './services/auth.service';
import { ActivatedRoute, NavigationEnd } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { filter, map, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LanguageService } from './services/language.service';
import { UserService } from './services/user.service';
import { SubmissionsService } from './services/submissions.service';
import { Store } from '@ngrx/store';
import { getCurrentUser } from './authModule/state/authentication.selectors';
import { BulkRegisterService } from './services/bulk-register.service';
import { environment } from './../environments/environment';

@Component({
   selector: 'app-root',
   templateUrl: './app.component.html',
   styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  currentLanguage: string = localStorage.getItem('websiteLanguage');
  currentUserId: string;
  showSpinner = false;
  downloadText = '';

  // Safety flags for token watcher
  private tokenWatcherInterval: any = null;
  private isWatchingForToken = false;
  private hasInitializedUser = false;

  constructor(
    public translate: TranslateService,
    private authService: AuthService,
    private titleService: Title,
    public router: Router,
    private activatedRoute: ActivatedRoute,
    private languageService: LanguageService,
    private userService: UserService,
    private store: Store,
    private bulkRegisterService: BulkRegisterService,
    private meta: Meta
  ) {
        if (environment.pilotApp) {
      this.meta.addTag({ name: 'robots', content: 'noindex' });
    }

    translate.addLangs([
      'english',
      'albanian',
      'german',
      'serbian',
      'polish',
      'french',
      'turkish',
      'italian',
      'czech',
      'greek',
      'portuguese',
      'spanish',
    ]);

    // translate.setDefaultLang('en');

    // const browserLang = translate.getBrowserLang();
    // translate.use(
      //    browserLang.match(/english|shqip|german/) ? browserLang : 'english'
      // );
  }

  ngOnInit(): void {
    // Check if we're in MFA verification mode
    if (this.isInMfaVerification()) {
      // During MFA verification, only initialize basic language support
      this.initializeMfaMode();
      return;
    }

    // Initialize user data if token exists but store is empty
    this.initializeUserDataFromToken();

    // Watch for token changes (for async token setting) - only if needed
    if (!this.hasInitializedUser) {
      this.setupTokenWatcher();
    }

    this.userService.spinnerObservable.subscribe(show => {
      this.showSpinner = show;
    });

    this.userService.messageObservable.subscribe(message => {
      this.downloadText = message;
    });
    // window.addEventListener('storage', (event) => {
      //    if (event.storageArea == localStorage) {
        //       let token = this.authService.getUserToken();
        //       if (
          //          token == undefined &&
          //          event.url.indexOf('individual') === -1 &&
          //          event.url.indexOf('admin') === -1
          //       ) {
            //          console.log('agneseeesaaa');

            //          this.authService.logout();
            //       }
            //    }
            // });

            this.languageService.selectedLanguage$.subscribe((language: string) => {

              this.currentLanguage = language;
              this.translate.use(language);
            });


    const appTitle = this.titleService.getTitle();

    this.router
      .events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          const child = this.activatedRoute.firstChild;

          if(child?.snapshot['_routerState']?.url?.split('individual/')[1]?.split('/')) {
             let componentNAme =  child.snapshot['_routerState'].url.split('individual/')[1].split('/');

             console.log(componentNAme,"componentNAme");


             componentNAme = componentNAme.map(element => {
                return element.charAt(0).toUpperCase() + element.slice(1);
             });
              return componentNAme.join(' ');

          }

          return appTitle


        })
      ).subscribe((ttl: string) => {
        this.titleService.setTitle(ttl);
      });
  }

  /**
   * Check if we're currently in MFA verification mode
   */
  private isInMfaVerification(): boolean {
    return this.router.url === '/mfa-verification' ||
           window.location.pathname === '/mfa-verification' ||
           localStorage.getItem('temp_mfa_data') !== null;
  }

  /**
   * Initialize minimal functionality during MFA verification
   */
  private initializeMfaMode(): void {
    // Set default language if none exists
    const defaultLanguage = this.currentLanguage || 'en';
    this.translate.use(defaultLanguage);

    // Listen for route changes to detect when MFA is complete
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // If we navigate away from MFA verification, initialize normally
      if (!this.isInMfaVerification()) {
        this.initializeUserDataFromToken();
        if (!this.hasInitializedUser) {
          this.setupTokenWatcher();
        }
      }
    });
  }

  /**
   * Setup watcher for async token availability - with safety checks
   */
  private setupTokenWatcher(): void {
    // Don't start token watcher during MFA verification
    if (this.isInMfaVerification()) {
      return;
    }

    // Prevent multiple watchers
    if (this.isWatchingForToken || this.tokenWatcherInterval) {
      return;
    }

    this.isWatchingForToken = true;
    let attempts = 0;
    const maxAttempts = 10; // Reduced to 5 seconds

    this.tokenWatcherInterval = setInterval(() => {
      attempts++;

      // Skip if we're in MFA verification
      if (this.isInMfaVerification()) {
        this.cleanupTokenWatcher();
        return;
      }

      const token = this.authService.getUserToken();
      const isLoggedIn = this.authService.isLoggedIn();

      if (isLoggedIn && token && !this.hasInitializedUser) {
        this.cleanupTokenWatcher();
        this.hasInitializedUser = true;

        // Check if store is still empty and initialize if needed
        this.store.select(getCurrentUser).pipe(take(1)).subscribe((userData) => {
          if (!userData) {
            this.store.dispatch(currentUser());
          }
        });
      } else if (attempts >= maxAttempts) {
        this.cleanupTokenWatcher();
      }
    }, 1000); // Increased to 1 second intervals
  }

  /**
   * Clean up token watcher safely
   */
  private cleanupTokenWatcher(): void {
    if (this.tokenWatcherInterval) {
      clearInterval(this.tokenWatcherInterval);
      this.tokenWatcherInterval = null;
    }
    this.isWatchingForToken = false;
  }

  /**
   * Initialize user data from token if user is logged in but store is empty
   */
  private initializeUserDataFromToken(): void {
    // Skip during MFA verification
    if (this.isInMfaVerification()) {
      return;
    }

    const token = this.authService.getUserToken();
    const isLoggedIn = this.authService.isLoggedIn();

    if (isLoggedIn && token) {
      this.hasInitializedUser = true;

      // Check if user data exists in store using take(1) to auto-unsubscribe
      this.store.select(getCurrentUser).pipe(take(1)).subscribe((userData) => {
        if (!userData) {
          this.store.dispatch(currentUser());
        }
      });
    }
  }

  /**
   * Cleanup on component destroy
   */
  ngOnDestroy(): void {
    this.cleanupTokenWatcher();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    // Check if registration is in progress
    const registrationState = this.bulkRegisterService.getCurrentProgress();

    if (registrationState.isInProgress) {
      // Cancel the event
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';

      // Show a confirmation dialog
      return 'Registration is in progress. Are you sure you want to leave? Your progress will be lost.';
    }
    return true;
  }

}
